// The portal API. Everything this app knows about identity and rooms comes from
// the portal — the browser never talks to Keycloak directly and never holds
// an OIDC token, only the portal's httpOnly session cookie.

/** Where the portal is mounted. Must match the module's `-path-prefix`. */
export const PREFIX = '/portal';

export interface Me {
  authenticated: boolean;
  name?: string;
  email?: string;
  guest?: boolean;
  op?: boolean;
  /** The Keycloak username operator matching is done against. */
  username?: string;
  /** The per-device LiveKit participant identity. */
  identity?: string;
}

export interface JoinGrant {
  /** wss:// URL of the SFU. */
  url: string;
  /** The LiveKit access token. Short-lived — fetch it when joining, never cache. */
  token: string;
  room: string;
  /** What a human called the meeting. Empty for a standing room. */
  title?: string;
  identity: string;
  name: string;
  role: 'participant' | 'op' | 'guest' | 'recorder';
  /** Checks that a tune request is the portal's (MWE-9). This browser's alone. */
  tune_key?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(PREFIX + path, {
    // The session lives in a __Host- cookie; without this the request is
    // anonymous and every call looks like a sign-in failure.
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    // The portal answers errors as {"error": "..."} — surface that text rather
    // than a bare status code, because "no such room" and "not signed in" want
    // very different things from the person reading it.
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) detail = body.error;
    } catch {
      // Non-JSON error body (an nginx page, say). Keep the status text.
    }
    throw new ApiError(detail, res.status);
  }
  return (await res.json()) as T;
}

/** Who the portal thinks we are. Never throws for "not signed in" — that is a state, not an error. */
export function fetchMe(): Promise<Me> {
  return getJSON<Me>('/api/me');
}

/** Ask for a way into a room. Requires a session; throws ApiError(401) without one. */
export function fetchJoinGrant(room: string): Promise<JoinGrant> {
  return getJSON<JoinGrant>(`/api/token?room=${encodeURIComponent(room)}`);
}

export interface InviteLink {
  url: string;
  room: string;
  /** RFC3339, UTC. */
  expiresAt: string;
}

/**
 * Mint a guest invite link for a room. Staff only — the portal refuses a guest,
 * so one invite can never become an unbounded supply of them.
 */
export async function createInvite(room: string, hours?: number): Promise<InviteLink> {
  const q = new URLSearchParams({ room });
  if (hours) q.set('hours', String(hours));
  const res = await fetch(`${PREFIX}/api/invite?${q}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) detail = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(detail, res.status);
  }
  return (await res.json()) as InviteLink;
}

export interface ShareLink {
  url: string;
  /** RFC3339, UTC. */
  expires: string;
}

/**
 * Mint a guest link to ONE meeting's written record. Operators only.
 *
 * The ordinary way a guest gets one of these is the summary mail, which issues
 * a link per recipient when a meeting is summarised. This is the supplement —
 * somebody who was not in the meeting, an address that was wrong, or a
 * deployment whose mail relay is not wired up yet.
 *
 * The URL comes back ONCE and is never stored: the server records that a link
 * exists and what became of it, not the token, so asking again mints a new one
 * rather than reading the old one out.
 */
export async function createShareLink(
  room: string,
  session: string,
  email?: string,
): Promise<ShareLink> {
  const q = new URLSearchParams();
  if (email) q.set('email', email);
  const res = await fetch(
    `${PREFIX}/api/meetings/${encodeURIComponent(room)}/${encodeURIComponent(session)}/share?${q}`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    },
  );
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) detail = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(detail, res.status);
  }
  return (await res.json()) as ShareLink;
}

/**
 * Send the browser through Keycloak, coming back to WHERE IT WAS.
 *
 * The return path matters: every summary email links to a specific meeting, and
 * without it signing in dumped you in the call instead of the thing you clicked.
 * The portal validates the path is local before honouring it.
 *
 * It carries no room. Signing in does not decide which meeting you are joining —
 * the front door is a dashboard, and the room is whatever you click there.
 */
export function signIn(): void {
  const back = window.location.pathname + window.location.search;
  window.location.href = `${PREFIX}/auth?return=${encodeURIComponent(back)}`;
}

export function signOutUrl(): string {
  return `${PREFIX}/logout`;
}

/** The server-rendered recordings archive: the raw per-track media files. */
export function archiveUrl(): string {
  return `${PREFIX}/recordings/`;
}

// --- live rooms ------------------------------------------------------------

export interface RoomState {
  name: string;
  /** What a human called it. Equal to `name` for a standing room. */
  title: string;
  /**
   * A meeting somebody started, as opposed to a room in the operator's
   * configuration. Shown differently: one is furniture, the other is happening
   * now and will be gone later.
   */
  adhoc?: boolean;
  /** At least one HUMAN is in it — the recorder does not count. */
  live: boolean;
  /** Display names, collapsed per person rather than per device. */
  people: string[];
  count: number;
  recording: boolean;
  /** When the meeting started, RFC3339. Only while it is live. */
  startedAt?: string;
}

/**
 * Start an ad-hoc meeting.
 *
 * The NAME comes back from the server, not from here: it is a path component,
 * a URL parameter and a LiveKit room key, and it carries a random suffix so two
 * people creating "standup" ten minutes apart do not land in each other's call.
 * All the browser supplies is what to call it.
 */
export async function createRoom(title: string): Promise<{ name: string; title: string }> {
  const res = await fetch(`${PREFIX}/api/rooms?title=${encodeURIComponent(title)}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) detail = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(detail, res.status);
  }
  return (await res.json()) as { name: string; title: string };
}

// --- what the recorder is writing, right now -------------------------------

export interface LiveTrack {
  /** The LiveKit participant identity — how a track joins back to a tile. */
  identity: string;
  principal?: string;
  name: string;
  file: string;
  kind: 'audio' | 'video';
  source: string;
  /** Size on disk at the moment of the request, so it ticks up. */
  bytes: number;
}

export interface RoomRecording {
  recording: boolean;
  session?: string;
  startedAt?: string;
  tracks: LiveTrack[];
}

/**
 * What is being written for this room, right now.
 *
 * The panel this feeds is the one thing on the front page of the website that
 * no other conferencing product could draw: four microphones, four files, named
 * for the people in the room. It is also the strongest disclosure in the
 * product — not "this meeting is being recorded" but "here is the file with
 * your name on it, and here is how big it is".
 */
export function fetchRoomRecording(room: string): Promise<RoomRecording> {
  return getJSON<RoomRecording>(`/api/rooms/${encodeURIComponent(room)}/recording`);
}

/**
 * Who is in which room, right now.
 *
 * Answers the question the old front door could only answer by joining: it put
 * you on camera first and let you look second.
 */
export function fetchRooms(): Promise<RoomState[]> {
  return getJSON<RoomState[]>('/api/rooms');
}

// --- meetings --------------------------------------------------------------

export interface TranscriptLine {
  /**
   * Seconds into the MEETING, not into the file. Named `t` because it is a
   * point on a timeline and a line has no end.
   */
  t: number;
  /** The account that said it. Absent on transcripts written before principals travelled with a line. */
  principal?: string;
  speaker: string;
  text: string;
  /**
   * File stem of the track it was heard on ("kami-mic-3"). One person can have
   * several tracks, one per join; absent on older transcripts.
   */
  track?: string;
}

/**
 * Recorded audio that has no text yet.
 *
 * `pending` and `shortfall` mean genuinely different things and the UI has to
 * say which — see MeetingStatus in Meetings.tsx. One resolves itself and one
 * never will.
 */
export interface TranscriptGap {
  /** Seconds into the meeting, the same clock a line uses. */
  t: number;
  seconds?: number;
  principal?: string;
  speaker: string;
  track: string;
  seq: number;
}

export interface Transcript {
  model: string;
  generated_at: string;
  lines: TranscriptLine[];
  /**
   * Segments queued for transcription. Non-empty means the meeting is still
   * being worked on and WILL finish — a count, not a fault.
   */
  pending?: TranscriptGap[];
  /** How many segments have produced text, per track. */
  transcribed?: Record<string, number>;
  /**
   * Segments the manifest says were recorded but which have neither text nor a
   * queue entry. Audio that existed and cannot be found: waiting does not fix
   * it, and the meeting will never summarise on its own.
   */
  shortfall?: number;
}

export interface MeetingTrack {
  file: string;
  kind: 'audio' | 'video';
  source: string;
  bytes?: number;
  sha256?: string;
  /**
   * Seconds after the meeting began that this track did. A transcript line's
   * time is on the meeting's clock and a player seeks on the file's; somebody
   * who joined eleven minutes late has a file whose zero is eleven minutes in.
   */
  offset: number;
  /** Media removed by a deletion request. The row survives the bytes. */
  erased?: boolean;
  /**
   * Seconds long: this track's own ended_at - started_at (MN-34). Absent while
   * it is still being recorded.
   */
  duration?: number;
  /**
   * A browser-playable URL for it, already prefixed (a WebM remux of the
   * original, served with Range). Absent when the server offers none.
   */
  play?: string;
}

export interface MeetingParticipant {
  principal: string;
  name: string;
  email?: string;
  /** Who vouched for them: the issuer host, or "invite". */
  authenticatedBy?: string;
  /** Only on a single-meeting fetch. */
  tracks?: MeetingTrack[];
}

export interface Erasure {
  principal: string;
  name?: string;
  at: string;
  by: string;
  reason?: string;
  tracks: string[];
  transcript_lines: number;
}

/**
 * What can be said about a manifest's signature.
 *
 * FOUR ANSWERS AND NOT A BOOLEAN. "unsigned" is a recording from an instance
 * with no key, "newer" is a document this build is not entitled to judge, and
 * only "invalid" is an allegation. Collapsing them is how a UI ends up
 * accusing an old archive of forgery.
 */
export interface SignatureState {
  state: 'valid' | 'invalid' | 'unsigned' | 'newer';
  key?: string;
  detail?: string;
}

export interface Meeting {
  room: string;
  session: string;
  title?: string;
  startedAt: string;
  endedAt?: string;
  participants: MeetingParticipant[];
  erasures?: Erasure[];
  signature?: SignatureState;
  /** Whether THIS caller may play the media back. A fact about the request. */
  playable?: boolean;
  /**
   * The legal holds covering this meeting, by id. Held material ignores the
   * retention policy, and an erasure against it is refused.
   */
  held?: string[];
  hasTranscript: boolean;
  hasSummary: boolean;
  /**
   * How many utterances were transcribed. Zero with hasTranscript means nobody
   * spoke — which is NOT "not transcribed yet", and is the only thing that
   * distinguishes a real summary from the stub every silent meeting gets.
   */
  transcriptLines: number;
  /**
   * The transcript's own accounting, on a LISTING as well as a detail fetch.
   *
   * `pending` is queued audio — progress, and it comes down on its own.
   * `shortfall` is audio with no text and no queue entry — a fault that waiting
   * does not fix. They must never be rendered the same way.
   *
   * OPTIONAL BECAUSE OF THE SERVER, NOT BECAUSE OF THE VALUE. Go sends both
   * without `omitempty`, so a portal that has them always sends them and 0
   * genuinely means "none". Absent therefore means the portal PREDATES them —
   * which is the case on a deployment running anything before ab5c520 — and the
   * UI must fall back rather than read the gap as "complete".
   */
  pending?: number;
  shortfall?: number;
  sentAt?: string;
  /** Only present on a single-meeting fetch. */
  summary?: string;
  /**
   * Present only on the DETAIL fetch. The listing deliberately omits it —
   * internal/meeting reads the transcript even for a list (to count lines) but
   * only attaches it when asked for content, so `pending` and `shortfall` are
   * not available on a row in the archive list.
   */
  transcript?: Transcript;
}

/** Past meetings, newest first. Staff only — the portal refuses a guest. */
/**
 * The archive.
 *
 * `mine` narrows it to the meetings you were actually in. A VIEW, not a
 * permission — the server decides what you may read either way, and this only
 * decides what is shown, so turning it off can never widen anything.
 */
export function fetchMeetings(mine = false): Promise<Meeting[]> {
  return getJSON<Meeting[]>(`/api/meetings${mine ? '?mine=1' : ''}`);
}

/**
 * One meeting, with its summary and transcript.
 *
 * `token` is a guest's signed link to this meeting, from their summary email.
 * They have no account to sign in with, so it is the only thing that gets them
 * in — and it is scoped to this one meeting, checked server-side against the
 * room and session in the URL.
 */
export function fetchMeeting(room: string, session: string, token?: string): Promise<Meeting> {
  const path = `/api/meetings/${encodeURIComponent(room)}/${encodeURIComponent(session)}`;
  return getJSON<Meeting>(token ? `${path}?t=${encodeURIComponent(token)}` : path);
}

function meetingPath(room: string, session: string): string {
  return `${PREFIX}/api/meetings/${encodeURIComponent(room)}/${encodeURIComponent(session)}`;
}

/** One track, for playback. Range-served, so seeking works. */
export function mediaUrl(room: string, session: string, file: string): string {
  return `${meetingPath(room, session)}/media/${encodeURIComponent(file)}`;
}

/** A transcript in a format some other tool takes. */
export type ExportFormat = 'json' | 'vtt' | 'srt' | 'md';

export function transcriptUrl(room: string, session: string, format: ExportFormat): string {
  return `${meetingPath(room, session)}/transcript/${format}`;
}

/** The whole session directory as a tar. Operators only, server-side. */
export function meetingArchiveUrl(room: string, session: string): string {
  return `${meetingPath(room, session)}/archive`;
}

/**
 * Remove one person's media and transcript lines from a meeting.
 *
 * It does NOT remove them from the participant list, and the UI says so before
 * asking: an archive that quietly drops somebody is an archive that has learnt
 * to lie about who was in the room.
 */
export async function erasePerson(
  room: string,
  session: string,
  principal: string,
  reason: string,
): Promise<Erasure> {
  const q = new URLSearchParams({ principal });
  if (reason) q.set('reason', reason);
  const res = await fetch(`${meetingPath(room, session)}/erase?${q}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) detail = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(detail, res.status);
  }
  return (await res.json()) as Erasure;
}

// --- search ----------------------------------------------------------------

export interface SearchHit {
  room: string;
  session: string;
  title?: string;
  startedAt: string;
  /** Seconds into the meeting — what a player seeks to. */
  at: number;
  principal?: string;
  speaker: string;
  text: string;
}

export interface SearchResults {
  hits: SearchHit[];
  /** Total matching lines, which can exceed hits.length. */
  lines: number;
  meetings: number;
  /**
   * The terms the SERVER matched. Highlighting uses these rather than
   * re-tokenising the query here and getting it subtly different.
   */
  terms: string[];
  scanned: number;
  tookMs: number;
  truncated: boolean;
}

export interface SearchQuery {
  q?: string;
  /** A principal, not a name: the filter is an equality test on an account. */
  speaker?: string;
  room?: string;
  days?: number;
}

export function search(query: SearchQuery): Promise<SearchResults> {
  const p = new URLSearchParams();
  if (query.q) p.set('q', query.q);
  if (query.speaker) p.set('speaker', query.speaker);
  if (query.room) p.set('room', query.room);
  if (query.days) p.set('days', String(query.days));
  return getJSON<SearchResults>(`/api/search?${p}`);
}

export interface Facets {
  speakers: { principal: string; name: string; meetings: number }[];
  rooms: string[];
}

/** Everyone and every room the archive knows about, for the filter chips. */
export function fetchFacets(): Promise<Facets> {
  return getJSON<Facets>('/api/search/facets');
}

// --- governance ------------------------------------------------------------
//
// How long it is kept, what must not be deleted yet, and who looked. Three
// endpoints because they are three answers an operator has to be able to give,
// and one page because they are one argument.

export interface RetentionPolicy {
  video: string;
  audio: string;
  transcript: string;
  meeting: string;
}

export interface Retention {
  default: RetentionPolicy;
  rooms: Record<string, RetentionPolicy>;
  /** False when the policy keeps everything — nothing is ever deleted. */
  active: boolean;
}

/** The policy in force. Readable by all staff: "how long is this kept" is a
 * question a participant is entitled to an answer to. */
export function fetchRetention(): Promise<Retention> {
  return getJSON<Retention>('/api/retention');
}

export interface Hold {
  id: string;
  reason: string;
  room?: string;
  principal?: string;
  from?: string;
  to?: string;
  placed_by: string;
  placed_at: string;
  lifted_at?: string;
  lifted_by?: string;
}

export function fetchHolds(): Promise<Hold[]> {
  return getJSON<Hold[]>('/api/holds');
}

export interface NewHold {
  reason: string;
  room?: string;
  principal?: string;
  /** YYYY-MM-DD. */
  from?: string;
  to?: string;
}

/**
 * POST (or DELETE) a path that carries its parameters in the query string, and
 * unwrap the portal's `{"error": "..."}` body.
 *
 * `method` is here rather than in a second helper because the only thing DELETE
 * changes is the verb — the credentials, the Accept header and the error
 * unwrapping are identical, and two copies of those is two places for them to
 * drift.
 */
async function post<T>(path: string, method: 'POST' | 'DELETE' = 'POST'): Promise<T> {
  const res = await fetch(PREFIX + path, {
    method,
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) detail = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(detail, res.status);
  }
  return (await res.json()) as T;
}

export function placeHold(h: NewHold): Promise<Hold> {
  const q = new URLSearchParams({ reason: h.reason });
  if (h.room) q.set('room', h.room);
  if (h.principal) q.set('principal', h.principal);
  if (h.from) q.set('from', h.from);
  if (h.to) q.set('to', h.to);
  return post<Hold>(`/api/holds?${q}`);
}

export function liftHold(id: string): Promise<Hold> {
  return post<Hold>(`/api/holds/${encodeURIComponent(id)}/lift`);
}

export interface AuditActor {
  principal?: string;
  name?: string;
  email?: string;
  guest?: boolean;
  op?: boolean;
}

export interface AuditEvent {
  ts: string;
  event: string;
  actor: AuditActor;
  ip?: string;
  room?: string;
  session?: string;
  outcome: 'allowed' | 'denied';
  reason?: string;
  detail?: Record<string, unknown>;
}

export interface AuditPage {
  configured: boolean;
  path?: string;
  events: AuditEvent[];
}

export function fetchAudit(limit = 200): Promise<AuditPage> {
  return getJSON<AuditPage>(`/api/audit?limit=${limit}`);
}

/** The raw JSONL. The same bytes a log shipper would read off disk. */
export function auditExportUrl(): string {
  return `${PREFIX}/api/audit/export`;
}

// --- diagnostics -----------------------------------------------------------

export interface Check {
  name: string;
  ok: boolean;
  detail: string;
  hint?: string;
}

export interface Status {
  checks: Check[];
  pipeline: {
    total: number;
    awaitingTranscript: number;
    awaitingSummary: number;
    awaitingDelivery: number;
    complete: number;
  };
  now: string;
}

/** Live health of the whole pipeline. Every check is active, not config-derived. */
export function fetchStatus(): Promise<Status> {
  return getJSON<Status>('/api/status');
}

/**
 * Create a synthetic meeting and let the real digest summarise and email it.
 * Operators only. Proves the summariser, the mail path and the rendering against
 * real credentials — the half a socket dial cannot check.
 */
export async function runSelfTest(): Promise<{ room: string; session: string; message: string }> {
  const res = await fetch(`${PREFIX}/api/selftest`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const b = (await res.json()) as { error?: string };
      if (b?.error) detail = b.error;
    } catch {
      /* non-JSON */
    }
    throw new ApiError(detail, res.status);
  }
  return (await res.json()) as { room: string; session: string; message: string };
}


// --- persistent rooms -------------------------------------------------------

/**
 * A room that outlives a call.
 *
 * DISTINCT FROM `RoomState`, which is "what is happening on the SFU right now".
 * This is the standing fact: somebody owns it, it survives being empty, and it
 * is the only kind of room that can carry a member list — an ad-hoc room cannot,
 * because it ceases to exist when the last person leaves.
 */
export interface SavedRoom {
  name: string;
  title: string;
  owner: string;
  /** 'staff' — anybody signed in; 'members' — only the people listed on it. */
  visibility: 'staff' | 'members';
  createdAt: string;
  /** A summary instruction applied to every meeting held here. */
  summaryPrompt?: string;
  /** The CALLER's role, so a row knows what to offer without a second request. */
  role?: 'admin' | 'member' | '';
}

export interface RoomMember {
  principal: string;
  role: 'admin' | 'member';
  addedBy: string;
  addedAt: string;
}

export function fetchSavedRooms(): Promise<SavedRoom[]> {
  return getJSON<SavedRoom[]>('/api/rooms/saved');
}

/** Build a query string, omitting the keys that were not supplied. */
function qs(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, v);
  const out = q.toString();
  return out ? `?${out}` : '';
}

export function createSavedRoom(
  title: string,
  visibility: 'staff' | 'members',
  prompt = '',
): Promise<SavedRoom> {
  return post<SavedRoom>(`/api/rooms/saved${qs({ title, visibility, prompt })}`);
}

export function updateSavedRoom(
  name: string,
  fields: { title?: string; visibility?: string; prompt?: string },
): Promise<SavedRoom> {
  return post<SavedRoom>(`/api/rooms/saved/${encodeURIComponent(name)}${qs(fields)}`);
}

export function deleteSavedRoom(name: string): Promise<{ deleted: string }> {
  return post<{ deleted: string }>(`/api/rooms/saved/${encodeURIComponent(name)}`, 'DELETE');
}

export function fetchRoomMembers(name: string): Promise<RoomMember[]> {
  return getJSON<RoomMember[]>(`/api/rooms/saved/${encodeURIComponent(name)}/members`);
}

export function setRoomMember(
  name: string,
  principal: string,
  role: 'admin' | 'member',
): Promise<RoomMember> {
  return post<RoomMember>(`/api/rooms/saved/${encodeURIComponent(name)}/members${qs({ principal, role })}`);
}

export function removeRoomMember(name: string, principal: string): Promise<{ removed: string }> {
  return post<{ removed: string }>(
    `/api/rooms/saved/${encodeURIComponent(name)}/members${qs({ principal })}`,
    'DELETE',
  );
}

// --- scheduled meetings -----------------------------------------------------

/** A meeting that has not happened yet. */
export interface Schedule {
  id: string;
  title: string;
  owner: string;
  room?: string;
  /** Ready to paste into an invitation — the room is minted when the series is booked. */
  joinUrl?: string;
  startsAt: string;
  /** The next occurrence. Absent once a series has ended or a one-off has passed. */
  nextStart?: string;
  minutes: number;
  tzid: string;
  /** RFC 5545, e.g. "FREQ=WEEKLY;BYDAY=TU". Absent for a one-off. */
  rrule?: string;
  remindMinutes: number;
  cancelled?: boolean;
  invitees: string[];
}

export interface NewSchedule {
  title: string;
  /** RFC3339, with an offset. */
  start: string;
  minutes: number;
  tzid: string;
  freq: '' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  interval: number;
  /** Weekly only: "MO,WE". */
  byday: string;
  invitees: string;
  remind: number;
  /** Reuse an existing room instead of minting one. */
  room?: string;
}

export function fetchSchedules(mine = false): Promise<Schedule[]> {
  return getJSON<Schedule[]>(`/api/schedules${mine ? '?mine=1' : ''}`);
}

export function createSchedule(s: NewSchedule): Promise<Schedule> {
  return post<Schedule>(
    `/api/schedules${qs({
      title: s.title,
      start: s.start,
      minutes: String(s.minutes),
      tzid: s.tzid,
      freq: s.freq,
      interval: String(s.interval),
      byday: s.byday,
      invitees: s.invitees,
      remind: String(s.remind),
      room: s.room,
    })}`,
  );
}

export function cancelSchedule(id: string): Promise<{ cancelled: string }> {
  return post<{ cancelled: string }>(`/api/schedules/${encodeURIComponent(id)}`, 'DELETE');
}
